import { CSSProperties, FC, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecentlyVisitedLocation } from 'shared/api/recently-visited-svc';
import { useInterval, useTrimmedElementCheck } from 'shared/lib/utils';
import { ProgramPillOutlined } from 'shared/ui/program-pill-outlined';
import { Tooltip } from 'shared/ui/tooltip';

import {
  getLocationDateISOString,
  getLocationDateUpdateInterval,
  getLocationFormattedDate,
} from '../../lib';
import { RecentlyVisitedLocationsDefaultThumbnail } from '../recently-visited-locations-default-thumbnail';

import styles from './styles.module.scss';

interface RecentlyVisitedLocationsCardProps {
  location: RecentlyVisitedLocation;
  widthInPercents: number;
}

export const RecentlyVisitedLocationsCard: FC<
  RecentlyVisitedLocationsCardProps
> = ({ location, widthInPercents }) => {
  const locationTitleRef = useRef<HTMLDivElement>(null);

  const locationTitleHeightTolerance = 1;
  const isLocationTitleTrimmed = useTrimmedElementCheck(
    locationTitleRef,
    locationTitleHeightTolerance,
  );

  const { t } = useTranslation();
  const [isThumbnailLoadingError, setIsThumbnailLoadingError] = useState(false);

  const { thumbnailImage, locationTitle, locationUrl, timestamp, productName } =
    location;

  const dateTime = getLocationDateISOString(timestamp);
  const initialDisplayedDate = getLocationFormattedDate(timestamp, t);

  const [displayedDate, setDisplayedDate] = useState(initialDisplayedDate);

  const dateUpdateInterval = getLocationDateUpdateInterval(timestamp);

  const updateDisplayedDate = () => {
    setDisplayedDate(getLocationFormattedDate(timestamp, t));
  };

  useInterval({
    callback: updateDisplayedDate,
    ms: dateUpdateInterval,
    dependencies: [dateUpdateInterval],
  });

  const shouldAbbreviateProductName = productName === 'Amplify Desmos Math';

  const handleThumbnailError = () => {
    setIsThumbnailLoadingError(true);
  };

  const handleCardKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();

      const eventTarget = e.target as HTMLAnchorElement;

      eventTarget.click();
    }
  };

  const cardStyles: CSSProperties = {
    width: `${widthInPercents}%`,
  };

  return (
    <a
      className={styles.card}
      style={cardStyles}
      href={locationUrl}
      onKeyDown={handleCardKeyDown}
      data-testid="recently-visited-locations-card"
    >
      <div className="d-flex">
        <div className={styles.thumbnailContainer}>
          {thumbnailImage && !isThumbnailLoadingError ? (
            <img
              className={styles.thumbnail}
              src={thumbnailImage}
              alt=""
              data-testid="recently-visited-locations-card-thumbnail"
              onError={handleThumbnailError}
            />
          ) : (
            <RecentlyVisitedLocationsDefaultThumbnail />
          )}
        </div>
        <Tooltip
          tooltipClassNames={styles.titleTooltip}
          title={locationTitle}
          suppress={!isLocationTitleTrimmed}
        >
          <div
            ref={locationTitleRef}
            className={styles.title}
            tabIndex={isLocationTitleTrimmed ? 0 : -1}
          >
            {locationTitle}
          </div>
        </Tooltip>
      </div>
      <div className={styles.footer}>
        <time className={styles.date} dateTime={dateTime}>
          {displayedDate}
        </time>
        {productName && (
          <ProgramPillOutlined
            programName={productName}
            abbreviate={shouldAbbreviateProductName}
          />
        )}
      </div>
    </a>
  );
};
